/* ==========================================================================
   Tanso Works — race personalization.

   No framework, no imports, no browser storage. Deferred.

   Failure modes this is built around (see NOTES.md §5):
     - Binding to the wrong cart-add form produces an order with no race
       details and NO error shown. Scope hard, verify, fail loudly.
     - Horizon submits through <product-form-component on:submit="/handleSubmit">,
       but that binding is a DELEGATED listener on `document` registered with
       { capture: true } (assets/component.js). It runs before any listener on
       the form, so no target-phase handler can stop it. Blocking is done with
       native constraint validation instead, which prevents the submit event
       from being dispatched at all. Nothing of ours is attached to the form.
       No page-wide interception, no capture listeners, no
       stopImmediatePropagation.
     - The theme's sticky bar is a puppet: it sets data-puppet on the real
       add-to-cart button and clicks it. Its puppet/added flags are only
       cleared on cartLinesUpdate/cartError, so a blocked submit would strand
       both buttons. We clear them ourselves on every block.
   ========================================================================== */
(function () {
  'use strict';

  var BOUND = new WeakSet();

  /** Motion is opt-out at the OS level, not a setting we own. */
  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Shown in the preview on the gift path. The gift buyer is the visitor
     least familiar with the product, so the plate stays on screen with
     example details rather than collapsing. These values are DISPLAY ONLY
     and are never written to any property input. Invented race name. */
  var EXAMPLE = {
    name: 'ALEX BROOKS',
    race: 'COASTLINE MARATHON',
    date: 'MARCH 15, 2026',
    time: '3:42:18',
    pace: '8:29 /mi',
    dist: '26.2'
  };
  var SECTION_EVENT = 'shopify:product:select'; // literal value of StandardEvents.productSelect

  /* --- helpers ---------------------------------------------------------- */

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  /**
   * Accepts 3:42:18, 3:42.18, 45:12 and bare digits like 34218 → seconds, or null.
   *
   * The bare-digit path is what makes this work on iOS unconditionally. An iOS
   * numeric keypad has no colon, and on some versions no period either, so a
   * phone buyer may be able to enter nothing but digits. Those are read
   * right-anchored — last two are seconds, the next two minutes, whatever
   * remains hours — which is how stopwatch and split entry has always worked:
   *
   *     342     → 3:42        (3 min 42 s)
   *     4512    → 45:12
   *     34218   → 3:42:18
   *     123456  → 12:34:56
   */
  function parseFinishTime(raw) {
    var s = String(raw || '').trim();
    if (!s) return null;

    if (/^\d+$/.test(s)) {
      // Under three digits is too ambiguous to guess at; over six is not a
      // race time. Both fall through to the validation message.
      if (s.length < 3 || s.length > 6) return null;
      var ds = parseInt(s.slice(-2), 10);
      var dm = parseInt(s.slice(-4, -2) || '0', 10);
      var dh = parseInt(s.slice(0, -4) || '0', 10);
      if (dm > 59 || ds > 59) return null;
      var dtotal = dh * 3600 + dm * 60 + ds;
      return dtotal > 0 ? dtotal : null;
    }

    var parts = s.split(/[:.]/).map(function (p) {
      return p.trim();
    });
    if (parts.length < 2 || parts.length > 3) return null;
    var nums = [];
    for (var i = 0; i < parts.length; i++) {
      if (!/^\d{1,3}$/.test(parts[i])) return null;
      nums.push(parseInt(parts[i], 10));
    }
    var h = 0;
    var m;
    var sec;
    if (nums.length === 3) {
      h = nums[0];
      m = nums[1];
      sec = nums[2];
    } else {
      m = nums[0];
      sec = nums[1];
    }
    if (m > 59 || sec > 59) return null;
    var total = h * 3600 + m * 60 + sec;
    return total > 0 ? total : null;
  }

  function formatDuration(totalSec) {
    var h = Math.floor(totalSec / 3600);
    var m = Math.floor((totalSec % 3600) / 60);
    var s = Math.round(totalSec % 60);
    if (s === 60) {
      s = 0;
      m += 1;
    }
    return h > 0 ? h + ':' + pad2(m) + ':' + pad2(s) : m + ':' + pad2(s);
  }

  function formatPaceValue(secPerUnit) {
    var m = Math.floor(secPerUnit / 60);
    var s = Math.round(secPerUnit % 60);
    if (s === 60) {
      s = 0;
      m += 1;
    }
    return m + ':' + pad2(s);
  }

  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  /** ISO yyyy-mm-dd → "March 15, 2026". Parsed as calendar parts, never as a
      UTC Date, so the day never shifts by timezone. */
  function formatDate(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').trim());
    if (!m) return '';
    var month = parseInt(m[2], 10);
    var day = parseInt(m[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return '';
    return MONTHS[month - 1] + ' ' + day + ', ' + m[1];
  }

  /* --- form resolution --------------------------------------------------- */

  var EXCLUDE = 'sticky-add-to-cart, dialog, cart-drawer-component, product-card, .quick-add-modal, .cart-drawer';

  /** `cart` on the product page, `claim` on the claim page. */
  function contextOf(root) {
    return root.getAttribute('data-race-context') === 'claim' ? 'claim' : 'cart';
  }

  /**
   * The claim page nests these fields inside a Shopify contact form, which is
   * already on the page and already has an id, so there is nothing to search
   * for: resolve it by id and then prove it is the right kind of form.
   *
   * Same standard as the cart path. Bind to the intended form or fail loudly;
   * never bind to whatever happens to be nearby.
   */
  function resolveContactForm(root) {
    var id = root.getAttribute('data-form-id');
    if (!id) return null;

    var form = document.getElementById(id);
    if (!form || form.tagName !== 'FORM') return null;

    // Must be the form belonging to this section, not one elsewhere on the page.
    var scope = root.closest('.shopify-section');
    if (!scope || !scope.contains(form)) return null;

    // getAttribute, NOT form.action: a form's named-control access shadows its
    // IDL properties, exactly as it does for .id (see the note below). An input
    // named "action" would make form.action return that element.
    var action = form.getAttribute('action');
    if (!action) return null;

    // Shopify renders `action="/contact#<form id>"`, so compare the path rather
    // than the raw string.
    var path;
    try {
      path = new URL(action, window.location.href).pathname;
    } catch (e) {
      return null;
    }
    if (path.replace(/\/+$/, '') !== '/contact') return null;

    return form;
  }

  /**
   * Resolve exactly one form for this customizer.
   * Returns null rather than guessing.
   */
  function resolveForm(root) {
    if (contextOf(root) === 'claim') return resolveContactForm(root);

    var scope = root.closest('.product-details') || root.closest('.shopify-section');
    if (!scope) return null;

    var candidates = Array.prototype.filter.call(
      scope.querySelectorAll('form[data-type="add-to-cart-form"]'),
      function (f) {
        return !f.closest(EXCLUDE);
      }
    );

    if (candidates.length !== 1) return null;

    var form = candidates[0];
    var expected = root.getAttribute('data-form-id');

    // getAttribute, NOT form.id: the cart form contains <input name="id">, and
    // a form's named-control access shadows .id with that element. form.id
    // returns the input, never the string, so a .id comparison can never pass.
    var actualId = form.getAttribute('id');

    // The `form` attribute on our inputs binds by id. If the id we rendered
    // does not match the form we found, the association is silently wrong.
    if (expected && actualId && actualId !== expected) return null;

    return form;
  }

  /* --- the block --------------------------------------------------------- */

  function reportBroken(root) {
    root.setAttribute('data-race-broken', 'true');
    console.error(
      '[race-customizer] Could not resolve exactly one add-to-cart form for this block. ' +
        'Expected form id "' + root.getAttribute('data-form-id') + '". ' +
        'Block rendered non-functional rather than binding to the wrong form.'
    );
  }

  function init(root) {
    // UI wiring happens once. Form binding is re-checked on every scan, because
    // a re-render can replace the <form> underneath a customizer that itself
    // survived (data-skip-node-update).
    if (BOUND.has(root)) {
      if (root.raceRebind) root.raceRebind();
      return;
    }

    var form = resolveForm(root);
    if (!form) {
      reportBroken(root);
      return;
    }

    BOUND.add(root);
    root.removeAttribute('data-race-broken');
    root.setAttribute('data-race-ready', 'true');

    var context = contextOf(root);
    var maxLen = parseInt(root.getAttribute('data-max-len'), 10) || 22;

    var els = {
      nowPanel: root.querySelector('[data-race-now-panel]'),
      laterPanel: root.querySelector('[data-race-later-panel]'),
      dot: root.querySelector('[data-race-dot]'),
      example: root.querySelector('[data-race-example]'),
      a11y: root.querySelector('[data-race-a11y]'),
      customPanel: root.querySelector('[data-race-custom-panel]'),
      customValue: root.querySelector('[data-race-field="customValue"]'),
      customUnit: root.querySelector('[data-race-field="customUnit"]'),
      outDate: root.querySelector('[data-race-out="date"]'),
      outPace: root.querySelector('[data-race-out="pace"]')
    };

    /* The finish time is three visible boxes writing into one hidden field.
       `fields.time` stays pointed at the hidden field so every reader below
       (the preview, the summary line, the pace) keeps getting one canonical
       string, exactly as it did when this was a single input. */
    var timeParts = {
      h: root.querySelector('[data-race-time="h"]'),
      m: root.querySelector('[data-race-time="m"]'),
      s: root.querySelector('[data-race-time="s"]')
    };

    /* Declared here, not beside the listeners that use it. `refreshConstraints`
       reads it and runs during setup, well before that point in the file, and
       a `var` hoisted but unassigned would have thrown on first render. */
    var timeOrder = ['h', 'm', 's'];

    var fields = {
      name: root.querySelector('[data-race-field="name"]'),
      race: root.querySelector('[data-race-field="race"]'),
      date: root.querySelector('[data-race-field="date"]'),
      time: root.querySelector('[data-race-field="time"]')
    };

    var modeInputs = root.querySelectorAll('[data-race-mode]');
    var distInputs = root.querySelectorAll('[data-race-dist-input]');

    /* --- preview ------------------------------------------------------- */

    var pv = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-race-pv]'), function (n) {
      pv[n.getAttribute('data-race-pv')] = n;
      n.setAttribute('data-race-base', n.getAttribute('font-size'));
    });

    /** Shrink SVG text until it fits its allotted width. Tested at max length
        on every field — a 22-character string must never overflow. */
    function fit(node) {
      var max = parseFloat(node.getAttribute('data-race-fit'));
      var base = parseFloat(node.getAttribute('data-race-base'));
      if (!max || !base) return;
      node.removeAttribute('textLength');
      node.removeAttribute('lengthAdjust');
      node.setAttribute('font-size', base);
      var len;
      try {
        len = node.getComputedTextLength();
      } catch (e) {
        return; // not rendered yet
      }
      if (len <= max) return;

      // Glyph advance is not perfectly linear in font-size, so a single
      // proportional pass can still land over budget. Refine, then stop at a
      // floor that keeps the lettering readable.
      var size = base;
      var floor = base * 0.34;
      for (var pass = 0; pass < 4 && len > max; pass++) {
        size = Math.max(size * (max / len) * 0.99, floor);
        node.setAttribute('font-size', size.toFixed(2));
        len = node.getComputedTextLength();
        if (size <= floor) break;
      }

      // Hard guarantee. At the floor a pathological string (22 capital W's)
      // can still exceed the budget; condense it to exactly the budget rather
      // than let it run past the edge of the plate.
      if (len > max) {
        node.setAttribute('textLength', max);
        node.setAttribute('lengthAdjust', 'spacingAndGlyphs');
      }
    }

    function setText(node, value, placeholder) {
      if (!node) return;
      var empty = !value;
      node.textContent = empty ? placeholder : value;
      // Never blank: empty fields show muted placeholder text in position.
      node.setAttribute('fill', empty ? '#9a9a9a' : '#ffffff');
      fit(node);
    }

    var dotTimer = null;
    function pulse() {
      if (!els.dot) return;
      els.dot.setAttribute('data-pulse', 'true');
      if (dotTimer) clearTimeout(dotTimer);
      dotTimer = setTimeout(function () {
        els.dot.removeAttribute('data-pulse');
      }, 450);
    }

    function selectedDistance() {
      for (var i = 0; i < distInputs.length; i++) {
        if (distInputs[i].checked) return distInputs[i];
      }
      return null;
    }

    /** Miles for the selected distance, or null. */
    function selectedMiles(input) {
      if (!input) return null;
      if (input.hasAttribute('data-race-custom')) {
        var v = parseFloat(els.customValue && els.customValue.value);
        if (!v || v <= 0) return null;
        return els.customUnit && els.customUnit.value === 'km' ? v * 0.621371 : v;
      }
      var m = parseFloat(input.getAttribute('data-race-miles'));
      return m > 0 ? m : null;
    }

    /* --- Route draw ---------------------------------------------------------
       The route traces itself once, the first time the plate is seen. It is
       not decoration: it is a race being run, shown at the moment the buyer is
       looking at the plate, which is the one claim the page most needs to
       land — this is your actual course.

       Once only. No loop, no replay on scroll. Skipped outright under reduced
       motion, where the line simply appears. ------------------------------ */

    var routeDrawn = false;

    /**
     * Return the route to its plain painted state. Clearing the inline dash
     * properties is what guarantees the line is visible: whatever happens to
     * the transition, the element ends up exactly as the markup drew it.
     */
    function settleRoute(route, ring) {
      route.style.transition = '';
      route.style.strokeDasharray = '';
      route.style.strokeDashoffset = '';
      if (ring) {
        ring.style.transition = '';
        ring.style.opacity = '';
      }
    }

    function drawRoute() {
      if (routeDrawn) return;
      var route = root.querySelector('[data-race-route]');
      var ring = root.querySelector('[data-race-startfinish]');
      if (!route) return;

      if (prefersReducedMotion()) {
        routeDrawn = true; // leave it drawn, never animate
        return;
      }

      // A hidden document paints nothing and CSS transitions do not progress.
      // Starting here would set the line to zero length and leave it stuck
      // invisible — worse than no animation. Wait until the page is actually
      // on screen.
      if (document.hidden) {
        document.addEventListener('visibilitychange', function onVisible() {
          if (document.hidden) return;
          document.removeEventListener('visibilitychange', onVisible);
          drawRoute();
        });
        return;
      }

      var len;
      try {
        len = route.getTotalLength();
      } catch (e) {
        routeDrawn = true;
        return; // not measurable; leave the line visible rather than hidden
      }
      if (!len) {
        routeDrawn = true;
        return;
      }

      routeDrawn = true;

      route.style.strokeDasharray = len;
      route.style.strokeDashoffset = len;
      if (ring) ring.style.opacity = '0';

      // Commit the starting state before transitioning off it.
      void route.getBoundingClientRect();

      // Timings, in one place so they can be tuned without reading the logic.
      //
      // The lead-in matters as much as the duration: the plate lands, the eye
      // settles on it, and only then does the line start moving. Starting on
      // arrival meant the draw was often half over before it was being looked
      // at. The delay is expressed on the transition rather than a timer, so
      // the route simply sits undrawn during it.
      var LEAD_IN = 850;
      var DRAW = 2015;
      var RING_IN = 340;
      var RING_AT = LEAD_IN + DRAW - 240; // the ring lands just before the line finishes

      route.style.transition =
        'stroke-dashoffset ' + DRAW + 'ms cubic-bezier(0.33, 0, 0.2, 1) ' + LEAD_IN + 'ms';
      route.style.strokeDashoffset = '0';

      if (ring) {
        ring.style.transition = 'opacity ' + RING_IN + 'ms ease-out ' + RING_AT + 'ms';
        ring.style.opacity = '1';
      }

      route.addEventListener(
        'transitionend',
        function () {
          settleRoute(route, ring);
        },
        { once: true }
      );

      // Hard guarantee. If the transition is interrupted, throttled, or never
      // fires at all, the route is still restored to fully drawn. Must outlast
      // the whole sequence, ring included, or it would cut the animation short.
      setTimeout(function () {
        settleRoute(route, ring);
      }, RING_AT + RING_IN + 700);
    }

    function watchForPlate() {
      var plate = root.querySelector('.race-cz__preview');
      if (!plate) return;

      // Already on screen at load — draw now rather than waiting on an observer.
      var box = plate.getBoundingClientRect();
      if (box.top < innerHeight && box.bottom > 0) {
        drawRoute();
        return;
      }

      if (!window.IntersectionObserver) {
        drawRoute();
        return;
      }

      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) {
          drawRoute();
          io.disconnect();
        }
      }, { threshold: 0.4 });
      io.observe(plate);
    }

    /* --- Pace reveal --------------------------------------------------------
       Pace is the one value calculated rather than asked for, and it used to
       just appear. A brief fade the first time it resolves is how the buyer
       notices we worked it out for them. Fires on the transition from unset to
       set, not on every keystroke. -------------------------------------- */

    var lastPaceShown = '';

    function revealPace() {
      if (prefersReducedMotion()) return;
      var node = pv.pace;
      if (!node) return;

      // Same reasoning as drawRoute: this dims the value before bringing it
      // back, so starting it where transitions cannot progress would leave the
      // pace stuck faded. Not worth animating in a document nobody is looking
      // at anyway.
      if (document.hidden) return;

      node.style.transition = 'none';
      node.style.opacity = '0.25';
      void node.getBoundingClientRect();
      node.style.transition = 'opacity 450ms ease-out';
      node.style.opacity = '1';

      var settle = function () {
        node.style.transition = '';
        node.style.opacity = '';
      };
      node.addEventListener('transitionend', settle, { once: true });
      // Guarantee the value ends fully legible whatever the transition does.
      setTimeout(settle, 1200);
    }

    function render() {
      // Gift path: the plate shows example details so the buyer can still see
      // what they are ordering. Nothing here touches the property inputs, and
      // the hidden outputs are deliberately left alone — they are disabled on
      // this path anyway, so example values could never be submitted.
      if (currentMode() === 'later') {
        if (els.example) els.example.hidden = false;
        setText(pv.name, EXAMPLE.name, 'RUNNER NAME');
        setText(pv.race, EXAMPLE.race, 'RACE NAME');
        setText(pv.date, EXAMPLE.date, 'RACE DATE');
        setText(pv.time, EXAMPLE.time, '--:--');
        setText(pv.pace, EXAMPLE.pace, '--:--');
        lastPaceShown = EXAMPLE.pace; // example data should not trigger the reveal
        setText(pv.dist, EXAMPLE.dist, '--');
        if (els.a11y) {
          els.a11y.textContent =
            'Example preview of the display plate. Your details are added later.';
        }
        refreshConstraints();
        return;
      }

      if (els.example) els.example.hidden = true;

      var dist = selectedDistance();
      var isCustom = !!(dist && dist.hasAttribute('data-race-custom'));

      if (els.customPanel) els.customPanel.hidden = !isCustom;

      // Keep the Custom radio's submitted value in sync with the number+unit,
      // so the right string submits with or without JS.
      if (isCustom) {
        var raw = parseFloat(els.customValue && els.customValue.value);
        var unit = (els.customUnit && els.customUnit.value) || 'mi';
        if (raw > 0) {
          var miles = unit === 'km' ? raw * 0.621371 : raw;
          dist.value =
            miles.toFixed(1) + ' mi' + (unit === 'km' ? ' (' + raw + ' km entered)' : '');
        } else {
          dist.value = 'Custom distance';
        }
      }

      setText(pv.name, (fields.name.value || '').trim().toUpperCase(), 'RUNNER NAME');
      setText(pv.race, (fields.race.value || '').trim().toUpperCase(), 'RACE NAME');
      setText(pv.date, (formatDate(fields.date.value) || '').toUpperCase(), 'RACE DATE');

      if (els.outDate) els.outDate.value = formatDate(fields.date.value);

      var secs = parseFinishTime(fields.time.value);
      setText(pv.time, secs ? formatDuration(secs) : '', '--:--');

      // Distance shown on the plate. Fixed distances carry their own label so
      // a 10K reads "10K" rather than "6.21" — the physical product is lettered
      // the way a runner would say it. The submitted property is unchanged.
      var distText = '';
      var miles = selectedMiles(dist);
      if (isCustom) {
        var cv = parseFloat(els.customValue && els.customValue.value);
        if (cv > 0) distText = String(cv);
      } else if (dist) {
        distText = dist.getAttribute('data-race-plate') || (miles ? String(parseFloat(miles.toFixed(2))) : '');
      }
      setText(pv.dist, distText, '--');

      // Pace: never asked, always derived.
      var paceOut = '';
      var paceShown = '';
      if (secs && miles) {
        var useKm = isCustom && els.customUnit && els.customUnit.value === 'km';
        var units = useKm ? miles / 0.621371 : miles;
        var per = secs / units;
        if (isFinite(per) && per > 0) {
          paceShown = formatPaceValue(per) + (useKm ? ' /km' : ' /mi');
          paceOut = paceShown;
        }
      }
      setText(pv.pace, paceShown, '--:--');
      if (paceShown && !lastPaceShown) revealPace();
      lastPaceShown = paceShown;
      if (els.outPace) els.outPace.value = paceOut;

      refreshConstraints();

      if (els.a11y) {
        els.a11y.textContent = paceShown
          ? 'Preview: ' + (fields.name.value || 'your name') + ', ' + (fields.race.value || 'your race') +
            ', finish time ' + (secs ? formatDuration(secs) : 'not set') + ', pace ' + paceShown
          : 'Preview of your display plate';
      }

      pulse();
    }

    /* --- character counters -------------------------------------------- */

    function counter(input) {
      var out = root.querySelector('[data-race-count-for="' + input.id + '"]');
      if (!out) return;
      var used = input.value.length;
      var left = maxLen - used;
      out.textContent = used + '/' + maxLen;
      out.setAttribute('data-state', left <= 0 ? 'full' : left <= 5 ? 'warn' : 'ok');
    }

    /* --- validation ------------------------------------------------------
       Blocking happens through NATIVE constraint validation, not through a
       submit listener.

       Horizon registers one delegated `submit` listener on `document` with
       { capture: true } (assets/component.js registerEventListeners), so its
       handleSubmit runs before any listener on the form itself. A target-phase
       listener cannot stop it, and the techniques that could — a capture
       listener of our own on window/document, or stopImmediatePropagation —
       are both ruled out, and would be page-wide interception besides.

       Native validation blocks the submit event from ever being dispatched, so
       Horizon's capture listener never runs and no cart request is made. We
       keep `required`, express the richer rules through setCustomValidity, and
       preventDefault the `invalid` events only to replace the browser's bubble
       with our own inline messages.

       This also means the no-JS path is enforced by the same mechanism. */

    var MSG = {
      name: 'Enter the runner’s name as it should appear on the plate.',
      race: 'Enter the race name as you’d like it lettered.',
      date: 'Pick the date the race was run.',
      time: 'Enter your finish time. Use 0 for hours on a shorter race.',
      timeRange: 'Minutes and seconds each need to be under 60.',
      timeZero: 'Enter a finish time greater than zero.',
      dist: 'Enter the distance you ran, then choose miles or kilometers.'
    };

    /** Push our non-HTML rules into the constraint API so the browser enforces them. */
    function refreshConstraints() {
      // Gift path: everything is disabled and therefore exempt. Clear any
      // custom validity first, or a hidden invalid field would block a submit
      // the buyer has no way to fix.
      if (currentMode() === 'later') {
        timeOrder.forEach(function (k) {
          if (timeParts[k]) timeParts[k].setCustomValidity('');
        });
        if (els.customValue) {
          els.customValue.setCustomValidity('');
          els.customValue.disabled = true;
        }
        if (els.customUnit) els.customUnit.disabled = true;
        return;
      }

      /* On the boxes, never on the hidden field: a hidden input is exempt from
         constraint validation, and marking one invalid blocks the submit with
         nothing the browser can focus or the buyer can see. */
      var hDigits = timeDigits(timeParts.h);
      var mDigits = timeDigits(timeParts.m);
      var sDigits = timeDigits(timeParts.s);
      // Hours is optional, so it is not part of this. Blank hours means zero.
      var anyBlank = !mDigits || !sDigits;
      var mNum = parseInt(mDigits || '0', 10);
      var sNum = parseInt(sDigits || '0', 10);
      var totalSec =
        parseInt(hDigits || '0', 10) * 3600 + mNum * 60 + sNum;

      timeOrder.forEach(function (k) {
        var box = timeParts[k];
        if (!box) return;
        var msg = '';
        if (!anyBlank) {
          if (k === 'm' && mNum > 59) msg = MSG.timeRange;
          else if (k === 's' && sNum > 59) msg = MSG.timeRange;
          else if (k === 's' && totalSec <= 0) msg = MSG.timeZero;
        }
        box.setCustomValidity(msg);
      });

      var dist = selectedDistance();
      var isCustom = !!(dist && dist.hasAttribute('data-race-custom'));

      // Disabled controls are exempt from validation AND are not submitted.
      // Without this, a hidden-but-invalid custom-distance field would block
      // the form with nothing visible to fix.
      if (els.customValue) {
        els.customValue.disabled = !isCustom;
        var v = parseFloat(els.customValue.value);
        els.customValue.setCustomValidity(isCustom && !(v > 0) ? MSG.dist : '');
      }
      if (els.customUnit) els.customUnit.disabled = !isCustom;
    }

    function setError(key, message) {
      var out = root.querySelector('[data-race-err="' + key + '"]');
      if (out) out.textContent = message || '';

      // The time's visible controls are the three boxes; its `fields` entry is
      // the hidden field, which no screen reader will ever land on.
      var targets =
        key === 'time'
          ? timeOrder.map(function (k) { return timeParts[k]; })
          : [fields[key] || (key === 'dist' ? selectedDistance() : null)];

      targets.forEach(function (input) {
        if (!input || !input.setAttribute) return;
        if (message) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      });
    }

    function clearError(key) {
      setError(key, '');
    }

    /* The browser fires `invalid` on every invalid control, in document order,
       then focuses the first. We collect them for this tick, then handle the
       first one once. `invalid` does not bubble, so these are attached per
       control — nothing page-wide. */

    var invalidBatch = [];
    var batchScheduled = false;

    function flushInvalid() {
      batchScheduled = false;
      var first = invalidBatch[0];
      invalidBatch = [];
      if (!first) return;

      // The theme's sticky bar sets data-added/data-puppet before the click
      // resolves and only clears them on a cart event, which a blocked submit
      // never fires. Clear them so neither button is left mid-animation.
      releaseButtons();
      // The sticky bar appends its animation after this task in some paths;
      // one more sweep before paint catches it without a visible flash.
      requestAnimationFrame(releaseButtons);

      var scrollTo = first.closest('.race-cz__field') || first;
      try {
        scrollTo.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch (e) {
        scrollTo.scrollIntoView();
      }
      if (first.focus) first.focus({ preventScroll: true });
    }

    function onInvalid(event) {
      event.preventDefault(); // suppress the browser bubble; keep the blocking

      var el = event.target;
      var key = el.getAttribute('data-race-field');
      if (!key && el.hasAttribute('data-race-time')) key = 'time';
      if (key === 'customValue') key = 'dist';
      if (key) setError(key, MSG[key] || el.validationMessage);

      invalidBatch.push(el);
      if (!batchScheduled) {
        batchScheduled = true;
        setTimeout(flushInvalid, 0);
      }
    }

    /* --- resetting the buttons after a blocked submit -------------------
       Horizon's sticky bar clears its puppet/added flags only when a cart
       event fires. A blocked submit fires none, so we clear them here or the
       bar keeps a checkmark for an add that never happened, and the real
       button never animates again. */

    function releaseButtons() {
      // Cart-only. The claim page has no add-to-cart button, no sticky bar and
      // no fly-to-cart, so there is nothing to release; the validation path
      // calls this unconditionally and must not throw here.
      if (context !== 'cart') return;

      var section = root.closest('.shopify-section') || document;
      var buttons = section.querySelectorAll(
        '[ref="addToCartButton"], .sticky-add-to-cart__button, .add-to-cart-button'
      );
      Array.prototype.forEach.call(buttons, function (b) {
        b.removeAttribute('data-added');
        if (b.dataset) b.dataset.puppet = 'false';
        b.disabled = false;
      });

      // Cancel the fly-to-cart animation.
      //
      // The theme's sticky bar creates a <fly-to-cart> element unconditionally:
      // sticky-add-to-cart.js handleAddToCartClick() clicks the real button and
      // then animates, without ever checking validity. The main button does
      // check (AddToCartComponent.handleClick bails on !checkValidity()), so
      // only the sticky path is affected. The result was an item visibly flying
      // into the cart on a submit that added nothing, which reads as the page
      // glitching rather than as a validation error.
      //
      // The element is appended after our invalid handler runs, so this is
      // called from the deferred flush rather than inline.
      Array.prototype.forEach.call(document.querySelectorAll('fly-to-cart'), function (el) {
        el.remove();
      });
    }

    /* --- gift path --------------------------------------------------------
       Disabled inputs are not submitted, so the order carries only
       `Personalization` with no empty keys cluttering it in admin. */

    function currentMode() {
      for (var i = 0; i < modeInputs.length; i++) {
        if (modeInputs[i].checked) return modeInputs[i].getAttribute('data-race-mode');
      }
      return 'now';
    }

    function applyMode() {
      var later = currentMode() === 'later';

      if (els.nowPanel) els.nowPanel.hidden = later;
      if (els.laterPanel) els.laterPanel.hidden = !later;

      // Disable by CONTAINER, not by name. The date picker is deliberately
      // unnamed (its human-readable twin carries the property), so a
      // name-based selector missed it: it stayed `required` on the gift path
      // and blocked the submit invisibly, because the panel it lives in is
      // hidden. Anything inside the collapsed panel should be inert.
      // The Personalization radios sit outside this panel, so they still submit.
      var owned = els.nowPanel
        ? els.nowPanel.querySelectorAll('input, select, textarea')
        : [];
      Array.prototype.forEach.call(owned, function (input) {
        input.disabled = later;
      });

      if (later) {
        ['name', 'race', 'date', 'time', 'dist'].forEach(clearError);
        releaseButtons();
      }

      // Repaint. The preview now stays on screen across both paths, so switching
      // mode has to swap it between the buyer's details and the example set.
      // render() calls refreshConstraints() itself.
      render();
    }

    /* --- listeners ------------------------------------------------------- */

    Array.prototype.forEach.call(modeInputs, function (i) {
      i.addEventListener('change', applyMode);
    });

    // An iOS numeric keypad has no colon, so a phone buyer can only type
    // "3.42.18". The parser already accepts that, but the raw text is what
    // submits as `Finish time` and would reach the packing slip unchanged.
    // Rewrite it to the canonical form once the field is left.
    function timeDigits(el) {
      return el && !el.disabled ? String(el.value || '').replace(/\D/g, '') : '';
    }

    /** Fold the three boxes into the hidden field, in the canonical form. */
    function normalizeTime() {
      if (!fields.time) return;
      var h = timeDigits(timeParts.h);
      var m = timeDigits(timeParts.m);
      var sec = timeDigits(timeParts.s);

      var before = fields.time.value;
      // Minutes and seconds are what make a time; hours is optional and blank
      // means zero. Without the m/s check, clearing minutes left 30 seconds
      // reading as "0:30" in the hidden field and in the preview, a time
      // nobody entered.
      if (!m || !sec) {
        fields.time.value = '';
      } else {
        var hi = parseInt(h || '0', 10);
        var mi = parseInt(m || '0', 10);
        var si = parseInt(sec || '0', 10);
        var total = hi * 3600 + mi * 60 + si;
        // Anything the boxes cannot express cleanly leaves the hidden field
        // empty; refreshConstraints is what reports why.
        fields.time.value = mi > 59 || si > 59 || total <= 0 ? '' : formatDuration(total);
      }
      if (fields.time.value !== before) render();
    }

    /* Typing moves you along. Two digits in hours takes you to minutes, and
       backspacing out of an empty box takes you back, so the whole time can be
       entered without ever reaching for the keyboard's navigation. */
    timeOrder.forEach(function (key, index) {
      var box = timeParts[key];
      if (!box) return;

      box.addEventListener('input', function () {
        var digits = String(box.value || '').replace(/\D/g, '').slice(0, 2);
        if (box.value !== digits) box.value = digits;
        normalizeTime();
        clearError('time');
        render();
        if (digits.length === 2 && index < timeOrder.length - 1) {
          var next = timeParts[timeOrder[index + 1]];
          if (next && !next.disabled) {
            next.focus();
            if (next.select) next.select();
          }
        }
      });

      box.addEventListener('keydown', function (event) {
        if (event.key !== 'Backspace' || box.value !== '' || index === 0) return;
        var prev = timeParts[timeOrder[index - 1]];
        if (!prev || prev.disabled) return;
        event.preventDefault();
        prev.focus();
        prev.value = String(prev.value || '').slice(0, -1);
        normalizeTime();
        render();
      });

      // Pad a single digit on the way out: 3, 4, 8 reads as 3:04:08.
      box.addEventListener('blur', function () {
        if (box.value.length === 1 && key !== 'h') box.value = '0' + box.value;
        normalizeTime();
        render();
      });
    });

    // Final guard: pointerdown on the add-to-cart button fires before the
    // field blurs, so the value is canonical before the form is read even if
    // the buyer taps straight from the keyboard. Scoped to the buttons in this
    // product's details container — nothing page-wide.
    // On the claim page the equivalent button is the one that opens the review
    // panel, marked with data-race-submit. Same reasoning either way: the value
    // must be canonical before anything reads the form.
    var btnScope = context === 'claim'
      ? root.closest('.shopify-section')
      : root.closest('.product-details') || root.closest('.shopify-section');
    var btnSelector = context === 'claim' ? '[data-race-submit]' : '.add-to-cart-button';
    if (btnScope) {
      Array.prototype.forEach.call(
        btnScope.querySelectorAll(btnSelector),
        function (b) {
          b.addEventListener('pointerdown', normalizeTime);
        }
      );
    }

    Object.keys(fields).forEach(function (key) {
      var input = fields[key];
      if (!input) return;
      input.addEventListener('input', function () {
        if (input.maxLength > 0) counter(input);
        clearError(key); // clear the moment the field is corrected
        render();
      });
    });

    Array.prototype.forEach.call(distInputs, function (i) {
      i.addEventListener('change', function () {
        clearError('dist');
        render();
        if (i.hasAttribute('data-race-custom') && els.customValue) els.customValue.focus();
      });
    });

    [els.customValue, els.customUnit].forEach(function (n) {
      if (!n) return;
      n.addEventListener('input', function () {
        clearError('dist');
        render();
      });
      n.addEventListener('change', render);
    });

    // `invalid` fires on the control, does not bubble, and is what the browser
    // dispatches instead of `submit` when the form is invalid.
    Array.prototype.forEach.call(
      root.querySelectorAll('input, select, textarea'),
      function (el) {
        el.addEventListener('invalid', onInvalid);
      }
    );

    /**
     * Track the currently-resolved form. Nothing is attached to it — blocking is
     * native — but a replaced form must still be re-verified so the block fails
     * loudly if a re-render ever leaves it pointing at the wrong one.
     */
    root.raceRebind = function () {
      var current = resolveForm(root);
      if (!current) {
        reportBroken(root);
        return;
      }
      root.removeAttribute('data-race-broken');
      root.raceForm = current;
    };

    root.raceForm = form;

    /* --- first paint ----------------------------------------------------- */

    [fields.name, fields.race].forEach(function (i) {
      if (i) counter(i);
    });
    applyMode();
    render();
    watchForPlate();

    // Fonts change text metrics, so re-fit once they land.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        Object.keys(pv).forEach(function (k) {
          fit(pv[k]);
        });
      });
    }
  }

  /* --- boot + re-bind ---------------------------------------------------- */

  function scan() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-race-customizer]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Horizon re-renders product details on state change and morphs nodes in
  // place. The customizer carries data-skip-node-update so morph leaves typed
  // input alone, but re-scan anyway: init() is idempotent via the WeakSet.
  document.addEventListener(SECTION_EVENT, function () {
    setTimeout(scan, 0);
  });
  document.addEventListener('shopify:section:load', scan);
})();
